import React, { useState, useEffect, useCallback } from 'react';
import { 
  Radio, 
  Volume2, 
  VolumeX, 
  Users, 
  Signal, 
  SignalZero,
  Search,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
  Mic,
  Play,
  Pause,
  X,
  Maximize2,
  Minimize2,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEED_TYPE_COLORS = {
  police: '#3b82f6',
  fire: '#f97316',
  combined: '#8b5cf6',
  ems: '#ef4444',
  sheriff: '#10b981',
  highway: '#eab308',
  federal: '#6b7280'
};

// Audio visualizer bars
const AudioVisualizer = ({ isPlaying, color = '#14b8a6' }) => {
  const [bars, setBars] = useState([20, 30, 40, 50, 30, 20, 40, 60, 30, 25, 45, 35]);
  
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setBars(prev => prev.map(() => Math.random() * 80 + 10));
    }, 100);
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  return (
    <div className="flex items-end gap-0.5 h-8">
      {bars.map((height, i) => (
        <div
          key={i}
          className="w-1 rounded-full transition-all duration-100"
          style={{
            height: isPlaying ? `${height}%` : '20%',
            backgroundColor: color,
            opacity: isPlaying ? 1 : 0.3
          }}
        />
      ))}
    </div>
  );
};

// Transcript item component
const TranscriptItem = ({ transcript, isNew }) => {
  const timeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all duration-300 ${
        isNew 
          ? 'bg-teal-500/10 border-teal-500/30 animate-pulse' 
          : 'bg-slate-800/50 border-slate-700/50'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Mic className="w-3 h-3 text-teal-400" />
          <span className="text-xs text-slate-400 font-mono">{transcript.precinct}</span>
        </div>
        <span className="text-xs text-slate-500">{timeAgo(transcript.timestamp)}</span>
      </div>
      <p className="text-sm text-slate-200 leading-relaxed">{transcript.text}</p>
      <div className="flex items-center gap-2 mt-2">
        <Badge variant="secondary" className="bg-slate-700/50 text-slate-400 text-[10px]">
          {Math.round(transcript.confidence * 100)}% confidence
        </Badge>
        <span className="text-[10px] text-slate-500">{transcript.duration_seconds}s</span>
      </div>
    </div>
  );
};

// Feed card component
const FeedCard = ({ feed, isSelected, onClick, isPlaying, onPlay }) => {
  const statusColors = {
    online: 'text-green-400',
    offline: 'text-red-400',
    maintenance: 'text-yellow-400'
  };
  
  const typeColor = FEED_TYPE_COLORS[feed.feed_type] || '#6b7280';
  
  return (
    <div 
      onClick={onClick}
      className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
        isSelected 
          ? 'bg-slate-800 border-teal-500/50 shadow-lg shadow-teal-500/10' 
          : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 hover:bg-slate-800/50'
      }`}
      data-testid={`feed-card-${feed.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${typeColor}20` }}
          >
            <Radio className="w-4 h-4" style={{ color: typeColor }} />
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-200 leading-tight">
              {feed.short_name || feed.precinct}
            </h4>
            <p className="text-xs text-slate-500">{feed.department}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {feed.status === 'online' ? (
            <Signal className={`w-4 h-4 ${statusColors[feed.status]}`} />
          ) : (
            <SignalZero className={`w-4 h-4 ${statusColors[feed.status]}`} />
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {feed.listeners.toLocaleString()}
          </span>
          <Badge 
            className="text-[10px] text-white"
            style={{ backgroundColor: typeColor }}
          >
            {feed.feed_type}
          </Badge>
        </div>
        
        {feed.status === 'online' && (
          <Button
            size="sm"
            variant="ghost"
            className={`h-7 w-7 p-0 ${isPlaying ? 'text-teal-400' : 'text-slate-400'}`}
            onClick={(e) => {
              e.stopPropagation();
              onPlay(feed);
            }}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
        )}
      </div>
      
      {/* Last transcript preview */}
      {feed.recent_transcripts?.[0] && (
        <div className="mt-2 pt-2 border-t border-slate-800">
          <p className="text-xs text-slate-400 line-clamp-2">
            {feed.recent_transcripts[0].text}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Scanner Feed component
const ScannerFeed = ({ isOpen, onClose, onMinimize, isMinimized, preselectedFeed }) => {
  const [feeds, setFeeds] = useState([]);
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [playingFeed, setPlayingFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Handle preselected feed from map click
  useEffect(() => {
    if (preselectedFeed && isOpen) {
      setSelectedFeed(preselectedFeed);
      if (preselectedFeed.city) {
        setSelectedCity(preselectedFeed.city);
      }
    }
  }, [preselectedFeed, isOpen]);

  // Fetch scanner data
  const fetchFeeds = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCity && selectedCity !== 'all') params.append('city', selectedCity);
      if (selectedType && selectedType !== 'all') params.append('feed_type', selectedType);
      if (selectedStatus && selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const response = await axios.get(`${API_URL}/api/scanners?${params.toString()}&limit=100`);
      setFeeds(response.data.feeds || []);
    } catch (error) {
      console.error('Failed to fetch scanner feeds:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedType, selectedStatus]);

  const fetchCities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scanners/cities`);
      setCities(response.data || []);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scanners/stats`);
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeeds();
    fetchCities();
    fetchStats();
    
    // Refresh feeds every 30 seconds
    const interval = setInterval(fetchFeeds, 30000);
    return () => clearInterval(interval);
  }, [fetchFeeds, fetchCities, fetchStats]);

  // Filter feeds by search
  const filteredFeeds = feeds.filter(feed => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      feed.name.toLowerCase().includes(query) ||
      feed.city.toLowerCase().includes(query) ||
      feed.precinct.toLowerCase().includes(query) ||
      feed.department.toLowerCase().includes(query)
    );
  });

  const handlePlay = (feed) => {
    if (playingFeed?.id === feed.id) {
      setPlayingFeed(null);
    } else {
      setPlayingFeed(feed);
      setSelectedFeed(feed);
    }
  };

  if (!isOpen) return null;

  // Minimized view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-[1100] glass-card border border-slate-700 rounded-lg p-3 w-80">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-teal-400" />
            <span className="text-sm font-medium text-slate-200">Scanner Feed</span>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onMinimize}>
              <Maximize2 className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={onClose}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {playingFeed ? (
          <div className="flex items-center gap-3">
            <AudioVisualizer isPlaying={true} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-200 truncate">{playingFeed.name}</p>
              <p className="text-[10px] text-slate-400">{playingFeed.listeners.toLocaleString()} listening</p>
            </div>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setPlayingFeed(null)}>
              <Pause className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <p className="text-xs text-slate-400">No feed playing</p>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className="fixed inset-0 z-[1100] bg-slate-950/95 backdrop-blur-sm overflow-hidden" data-testid="scanner-feed-panel">
      {/* Header */}
      <div className="h-16 border-b border-slate-800 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-teal-400" />
            <h2 className="text-lg font-semibold text-slate-100">Police Scanner Feeds</h2>
          </div>
          
          {/* Stats */}
          <div className="hidden md:flex items-center gap-4 ml-8">
            <div className="flex items-center gap-2 text-sm">
              <Signal className="w-4 h-4 text-green-400" />
              <span className="text-slate-400">{stats.online_feeds || 0}</span>
              <span className="text-slate-600">online</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-teal-400" />
              <span className="text-slate-400">{(stats.active_listeners || 0).toLocaleString()}</span>
              <span className="text-slate-600">listening</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-purple-400" />
              <span className="text-slate-400">{stats.total_cities || 0}</span>
              <span className="text-slate-600">cities</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onMinimize}>
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar - Feed List */}
        <div className="w-80 border-r border-slate-800 flex flex-col">
          {/* Filters */}
          <div className="p-4 border-b border-slate-800 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search feeds..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-700"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="flex-1 bg-slate-900 border-slate-700 h-8 text-xs">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent className="glass-card border-slate-700">
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.slice(0, 20).map(city => (
                    <SelectItem key={city.city} value={city.city}>
                      {city.city} ({city.precinct_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-24 bg-slate-900 border-slate-700 h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="glass-card border-slate-700">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Feed list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full" />
              </div>
            ) : filteredFeeds.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No feeds found</p>
              </div>
            ) : (
              filteredFeeds.map(feed => (
                <FeedCard
                  key={feed.id}
                  feed={feed}
                  isSelected={selectedFeed?.id === feed.id}
                  isPlaying={playingFeed?.id === feed.id}
                  onClick={() => setSelectedFeed(feed)}
                  onPlay={handlePlay}
                />
              ))
            )}
          </div>
        </div>

        {/* Main content - Selected feed */}
        <div className="flex-1 flex flex-col">
          {selectedFeed ? (
            <>
              {/* Selected feed header */}
              <div className="p-6 border-b border-slate-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{selectedFeed.name}</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {selectedFeed.city}, {selectedFeed.state} • {selectedFeed.area}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Users className="w-4 h-4" />
                        {selectedFeed.listeners.toLocaleString()} listeners
                      </div>
                      <p className="text-xs text-slate-500">Peak: {selectedFeed.peak_listeners.toLocaleString()}</p>
                    </div>
                    
                    <Button
                      size="lg"
                      className={`${
                        playingFeed?.id === selectedFeed.id 
                          ? 'bg-teal-600 hover:bg-teal-500' 
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                      onClick={() => handlePlay(selectedFeed)}
                      disabled={selectedFeed.status !== 'online'}
                    >
                      {playingFeed?.id === selectedFeed.id ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Listen
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Audio visualizer */}
                {playingFeed?.id === selectedFeed.id && (
                  <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                        <Volume2 className="w-5 h-5 text-teal-400 animate-pulse" />
                      </div>
                      <AudioVisualizer isPlaying={true} />
                      <div className="flex-1">
                        <p className="text-sm text-slate-200">Now Playing</p>
                        <p className="text-xs text-slate-400">{selectedFeed.department}</p>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400">LIVE</Badge>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Transcripts */}
              <div className="flex-1 overflow-y-auto p-6">
                <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-teal-400" />
                  Recent Transmissions
                </h4>
                
                <div className="space-y-3">
                  {selectedFeed.recent_transcripts?.map((transcript, index) => (
                    <TranscriptItem 
                      key={transcript.id} 
                      transcript={transcript} 
                      isNew={index === 0 && playingFeed?.id === selectedFeed.id}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Radio className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-400">Select a Scanner Feed</h3>
                <p className="text-sm text-slate-500 mt-1">Choose a feed from the list to view details and listen</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScannerFeed;
